import type { BookingDraft } from '@/scopes/checkout/domain/types'
import { useBookingNumber } from '@/domain/groups/hooks'
import { createSelectItemEvent } from '@/scopes/checkout/domain/analytics'
import { useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import {
  useBookingDraftQueryKey,
  useCheckoutSid,
} from '@/scopes/checkout/domain/hooks'
import {
  updateRoomInAvailableRooms,
  updateRoomInBookingDraft,
} from '@/scopes/checkout/groups/domain/actions/availableRooms'
import { mapRoomSelectionToGA4Item } from '@/scopes/checkout/groups/domain/analyticsMapper'
import { AvailableRooms, UpdateRoomSelectionPayload } from '@/scopes/checkout/groups/domain/types/availableRooms'
import { groupsCheckoutAPIClient } from '@/scopes/checkout/groups/infrastructure/api'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

export function useAvailableRoomsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const queryKey = useAvailableRoomsQueryKey()
  const masterBookingNumber = useBookingNumber()

  const availableRoomsQuery = useQuery<AvailableRooms>({
    queryKey,
    queryFn: async () => {
      return groupsCheckoutAPIClient.getAvailableRooms(masterBookingNumber)
    },
    enabled,
    refetchOnMount: false,
    retry: false,
  })

  return {
    ...availableRoomsQuery,
  }
}

export function useRoom() {
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const queryKey = useAvailableRoomsQueryKey()
  const bookingDraftQueryKey = useBookingDraftQueryKey()
  const { contextItemId } = useCheckoutContext()
  const { sendEvent } = useAnalytics()

  const setRoomMutation = useMutation({
    mutationFn: async (updatedRoom: UpdateRoomSelectionPayload) => {
      return groupsCheckoutAPIClient.setRoomSelection({ sid, contextItemId }, updatedRoom)
    },
    onMutate: async (updatedRoom: UpdateRoomSelectionPayload) => {
      await queryClient.cancelQueries({ queryKey })
      const previousRooms = queryClient.getQueryData(queryKey)

      if (updatedRoom.isNewRoom || updatedRoom.assignationMethod === 'random') {
        queryClient.setQueryData(queryKey, updateRoomInAvailableRooms(updatedRoom))
        queryClient.setQueryData(bookingDraftQueryKey, updateRoomInBookingDraft(updatedRoom))
      }

      return { previousRooms }
    },
    onError: async (_, __, context) => {
      if (!context) {
        return
      }
      queryClient.setQueryData(queryKey, context.previousRooms)
      await queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: async (_, updatedRoom) => {
      const availableRooms = queryClient.getQueryData<AvailableRooms>(queryKey)
      const bookingDraft = queryClient.getQueryData<BookingDraft>(bookingDraftQueryKey)
      const item = mapRoomSelectionToGA4Item(updatedRoom, availableRooms, bookingDraft?.analytics)

      if (item) {
        sendEvent(createSelectItemEvent([item], bookingDraft?.analytics))
      }

      await queryClient.invalidateQueries({ queryKey })
      await queryClient.invalidateQueries({ queryKey: bookingDraftQueryKey })
    },
  })

  return {
    setRoomValue: setRoomMutation.mutate,
    setRoomValueAsync: setRoomMutation.mutateAsync,
  }
}

export function useAvailableRoomsQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['available-rooms', sid], [sid])
}
